<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { backend_url } from '@/config/config';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';

const route = useRoute();
const fromLogin = computed(() => route.query.from === 'login');

const name = ref('');
const email = ref('');
const message = ref('');
const loading = ref(false);
const success = ref(false);
const error = ref('');

const submit = async () => {
  error.value = '';
  loading.value = true;
  try {
    await axios.post(`${backend_url}/contact`, {
      name: name.value,
      email: email.value,
      message: message.value,
    });
    success.value = true;
  } catch {
    error.value = 'Une erreur est survenue. Veuillez réessayer.';
  } finally {
    loading.value = false;
  }
};
</script>

<template>

  <!-- ── Mode carte (depuis la page de connexion) ── -->
  <div v-if="fromLogin" class="dh-page">
    <div class="dh-wrapper">

      <div class="dh-header">
        <svg class="dh-target-bg" viewBox="-140 -140 280 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="0" cy="0" r="140" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.35)" stroke-width="1.2" />
          <circle cx="0" cy="0" r="124" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
          <circle cx="0" cy="0" r="82" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.35)" stroke-width="1" />
          <circle cx="0" cy="0" r="72" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
          <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
          <circle cx="0" cy="0" r="6" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(0)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(18)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(36)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(54)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(72)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(90)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(108)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(126)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(144)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(162)" />
        </svg>
        <div class="dh-header-content">
          <div class="dh-brand">
            <img :src="dartsHubLogo" alt="DartsHub" class="dh-brand-logo" />
          </div>
          <div class="dh-tricolor-bar">
            <span></span><span></span><span></span>
          </div>
          <p class="dh-subtitle">Nous contacter</p>
        </div>
      </div>

      <div class="dh-body">
        <div v-if="success" class="dh-success">
          <i class="fa-solid fa-circle-check" />
          <p>Votre message a bien été envoyé. Nous vous répondrons rapidement.</p>
        </div>

        <form v-else class="dh-form" @submit.prevent="submit">
          <div class="dh-field">
            <label>Nom</label>
            <input v-model="name" type="text" placeholder="Votre nom" required class="dh-input" />
          </div>
          <div class="dh-field">
            <label>Email</label>
            <input v-model="email" type="email" placeholder="votre@email.fr" required class="dh-input" />
          </div>
          <div class="dh-field">
            <label>Message</label>
            <textarea v-model="message" placeholder="Votre message..." required rows="5" class="dh-input dh-textarea" />
          </div>
          <div v-if="error" class="dh-msg dh-msg--error">{{ error }}</div>
          <button type="submit" class="dh-btn dh-btn-primary" :disabled="loading">
            <i v-if="loading" class="fa-solid fa-spinner fa-spin" />
            <span>{{ loading ? 'Envoi…' : 'Envoyer le message' }}</span>
          </button>
        </form>
      </div>

      <div class="dh-footer">
        <span>Authentification sécurisée par</span>
        <strong>&nbsp;DartsHub</strong>
        <span class="dh-footer-sep">·</span>
        <RouterLink to="/login" class="dh-footer-link">
          <i class="fa-solid fa-arrow-left" />
          Retour à la connexion
        </RouterLink>
      </div>

    </div>
  </div>

  <!-- ── Mode pleine page (depuis la page d'accueil) ── -->
  <div v-else class="lp-page">

    <header class="lp-hero">
      <svg class="lp-target-bg" viewBox="-140 -140 280 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="0" cy="0" r="140" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.35)" stroke-width="1.2" />
        <circle cx="0" cy="0" r="124" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
        <circle cx="0" cy="0" r="82" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.35)" stroke-width="1" />
        <circle cx="0" cy="0" r="72" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
        <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
        <circle cx="0" cy="0" r="6" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(0)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(18)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(36)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(54)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(72)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(90)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(108)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(126)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(144)" />
        <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8" transform="rotate(162)" />
      </svg>
      <div class="lp-hero-content">
        <RouterLink to="/" class="lp-brand-logo-link"><img :src="dartsHubLogo" alt="DartsHub" class="lp-brand-logo" /></RouterLink>
        <div class="lp-tricolor-bar"><span></span><span></span><span></span></div>
        <p class="lp-tagline">Nous contacter</p>
      </div>
    </header>

    <section class="lp-section">
      <div v-if="success" class="lp-success">
        <i class="fa-solid fa-circle-check" />
        <p>Votre message a bien été envoyé. Nous vous répondrons rapidement.</p>
      </div>

      <form v-else class="lp-form" @submit.prevent="submit">
        <div class="lp-field">
          <label>Nom</label>
          <input v-model="name" type="text" placeholder="Votre nom" required class="lp-input" />
        </div>
        <div class="lp-field">
          <label>Email</label>
          <input v-model="email" type="email" placeholder="votre@email.fr" required class="lp-input" />
        </div>
        <div class="lp-field">
          <label>Message</label>
          <textarea v-model="message" placeholder="Votre message..." required rows="6" class="lp-input lp-textarea" />
        </div>
        <div v-if="error" class="lp-msg lp-msg--error">{{ error }}</div>
        <button type="submit" class="lp-btn lp-btn-primary" :disabled="loading">
          <i v-if="loading" class="fa-solid fa-spinner fa-spin" />
          <span>{{ loading ? 'Envoi…' : 'Envoyer le message' }}</span>
        </button>
      </form>
    </section>

    <Footer backLink />

  </div>

</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

/* ── Shared ── */
* { box-sizing: border-box; }

/* ── Card mode (from login) ── */
.dh-page { min-height: 100svh; display: flex; align-items: center; justify-content: center; padding: 2rem 1rem; background: var(--bg); }
.dh-wrapper { display: flex; flex-direction: column; font-family: 'Poppins', 'Segoe UI', sans-serif; background: var(--bg-card); color: var(--text); border-radius: 20px; overflow: hidden; width: 420px; max-width: 100%; box-shadow: 0 8px 40px color-mix(in srgb, var(--primary) 10%, transparent); }
.dh-header { background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%); position: relative; text-align: center; overflow: hidden; }
.dh-target-bg { position: absolute; top: 50%; right: -30px; transform: translateY(-50%); width: 240px; height: 240px; pointer-events: none; opacity: 0.7; }
.dh-header-content { position: relative; z-index: 1; padding: 22px 28px 16px; }
.dh-brand { display: flex; justify-content: center; align-items: center; margin-bottom: 8px; }
.dh-brand-logo { height: 2.25rem; width: auto; }
.dh-tricolor-bar { display: flex; height: 4px; width: 110px; margin: 0 auto 12px; border-radius: 10px; overflow: hidden; gap: 3px; }
.dh-tricolor-bar span { flex: 1; border-radius: 10px; }
.dh-tricolor-bar span:nth-child(1) { background: var(--dh-bar-1); }
.dh-tricolor-bar span:nth-child(2) { background: var(--dh-bar-2); }
.dh-tricolor-bar span:nth-child(3) { background: var(--primary); }
.dh-subtitle { font-size: 0.76rem; color: rgba(255, 255, 255, 0.7); margin: 0 0 14px; letter-spacing: 0.2px; font-weight: 300; }
.dh-body { padding: 24px 28px; flex: 1; }
.dh-form { display: flex; flex-direction: column; gap: 14px; }
.dh-field { display: flex; flex-direction: column; gap: 5px; }
.dh-field label { font-size: 0.8rem; font-weight: 600; color: var(--text-muted); }
.dh-input { width: 100%; padding: 10px 14px; border: 2px solid var(--border); border-radius: 12px; background: var(--bg-input); color: var(--text); font-size: 0.92rem; font-family: inherit; transition: border-color 0.2s, box-shadow 0.2s, background 0.2s; box-sizing: border-box; }
.dh-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 10%, transparent); background: var(--bg-card); }
.dh-textarea { resize: vertical; min-height: 120px; }
.dh-btn { width: 100%; padding: 12px 16px; border: none; border-radius: 12px; font-size: 0.9rem; font-weight: 600; font-family: inherit; cursor: pointer; transition: all 0.2s ease; text-align: center; display: flex; align-items: center; justify-content: center; gap: 8px; }
.dh-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.dh-btn-primary { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%); color: #ffffff; box-shadow: 0 4px 14px color-mix(in srgb, var(--primary) 28%, transparent); }
.dh-btn-primary:hover:not(:disabled) { background: linear-gradient(135deg, var(--primary-hover) 0%, color-mix(in srgb, var(--primary) 60%, #000) 100%); transform: translateY(-1px); }
.dh-msg { font-size: 0.83rem; font-weight: 500; text-align: center; padding: 10px 14px; border-radius: 10px; }
.dh-msg--error { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }
.dh-success { text-align: center; padding: 16px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.dh-success i { font-size: 2.5rem; color: #16a34a; }
.dh-success p { color: var(--text-muted); font-size: 0.9rem; line-height: 1.6; margin: 0; }
.dh-footer { background: var(--bg-input); border-top: 1px solid var(--border); padding: 10px 16px; text-align: center; font-size: 0.72rem; color: var(--text-muted); font-family: 'Poppins', 'Segoe UI', sans-serif; display: flex; align-items: center; justify-content: center; gap: 4px; flex-wrap: wrap; }
.dh-footer strong { color: var(--primary); font-weight: 600; }
.dh-footer-sep { margin: 0 4px; color: #cbd5e1; }
.dh-footer-link { color: var(--primary); font-weight: 600; text-decoration: none; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 5px; }
.dh-footer-link:hover { text-decoration: underline; }

/* ── Full page mode (from landing) ── */
.lp-page { font-family: 'Poppins', 'Segoe UI', sans-serif; background: var(--bg); min-height: 100svh; display: flex; flex-direction: column; }
.lp-hero { background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%); position: relative; overflow: hidden; text-align: center; padding: 56px 24px 48px; }
.lp-target-bg { position: absolute; top: 50%; right: -60px; transform: translateY(-50%); width: 320px; height: 320px; pointer-events: none; opacity: 0.5; }
.lp-hero-content { position: relative; z-index: 1; max-width: 600px; margin: 0 auto; }
.lp-brand-logo-link { display: block; margin-bottom: 12px; }
.lp-brand-logo { height: clamp(2.5rem, 6vw, 3.5rem); width: auto; }
.lp-tricolor-bar { display: flex; height: 4px; width: 120px; margin: 0 auto 20px; border-radius: 10px; overflow: hidden; gap: 3px; }
.lp-tricolor-bar span { flex: 1; border-radius: 10px; }
.lp-tricolor-bar span:nth-child(1) { background: var(--dh-bar-1); }
.lp-tricolor-bar span:nth-child(2) { background: var(--dh-bar-2); }
.lp-tricolor-bar span:nth-child(3) { background: var(--dh-bar-3); }
.lp-tagline { color: rgba(255,255,255,0.8); font-size: clamp(0.9rem, 2.5vw, 1.1rem); font-weight: 300; margin: 0; }
.lp-section { padding: 48px 24px; max-width: 560px; margin: 0 auto; width: 100%; flex: 1; }
.lp-form { display: flex; flex-direction: column; gap: 16px; }
.lp-field { display: flex; flex-direction: column; gap: 6px; }
.lp-field label { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); }
.lp-input { width: 100%; padding: 11px 16px; border: 2px solid var(--border); border-radius: 12px; background: var(--bg-card); color: var(--text); font-size: 0.92rem; font-family: inherit; transition: border-color 0.2s, box-shadow 0.2s; box-sizing: border-box; }
.lp-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 10%, transparent); }
.lp-textarea { resize: vertical; min-height: 140px; }
.lp-btn { width: 100%; padding: 13px 16px; border: none; border-radius: 12px; font-size: 0.95rem; font-weight: 600; font-family: inherit; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 8px; }
.lp-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.lp-btn-primary { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%); color: #ffffff; box-shadow: 0 4px 18px color-mix(in srgb, var(--primary) 28%, transparent); }
.lp-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 22px color-mix(in srgb, var(--primary) 36%, transparent); }
.lp-msg { font-size: 0.85rem; font-weight: 500; text-align: center; padding: 10px 14px; border-radius: 10px; }
.lp-msg--error { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }
.lp-success { text-align: center; padding: 32px 0; display: flex; flex-direction: column; align-items: center; gap: 14px; }
.lp-success i { font-size: 3rem; color: #16a34a; }
.lp-success p { color: var(--text-muted); font-size: 0.95rem; line-height: 1.6; margin: 0; }
</style>
